/**
 * PUBLIC HOLIDAYS — the supply route, as a screen.
 *
 * Migration 57 ships `public_holidays` EMPTY by owner instruction (*"do not
 * guess a single date"*), and until this page existed the only way to fill it
 * was SQL: *"I would be pasting INSERTs for six countries."* This is the page
 * that answer needed.
 *
 * ⚠ TWO PANELS, TWO DIFFERENT PERMISSIONS, AND THAT IS NOT AN ACCIDENT.
 * The calendar itself is country CONFIGURATION (`config.manage`, country-scoped
 * — a Country Manager maintains their own and nobody else's). The gap list is a
 * property of a PERSON and is fixed by editing that person, so it rides
 * `users.manage`. A seat holding one and not the other sees only its half, and
 * the half it cannot use is not rendered — never rendered-and-disabled, which
 * is the house rule for a control a seat may not operate.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';

import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, EmptyState, SkeletonRows, TextField } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  adminInfraKeys,
  fetchCalendarGaps,
  fetchHolidays,
  replaceHolidays,
  setUserCalendar,
} from './infra-api';
import type { CalendarGapDto, HolidayCalendarDto, HolidayDto } from './infra-api';
import { NoAdminAccess, errorSentence } from './states';


/**
 * A pasted line to a holiday.
 *
 * ⚠ THE PARSER IS DELIBERATELY FORGIVING ABOUT SHAPE AND STRICT ABOUT DATES.
 * An owner pasting a year of holidays has them in whatever a government website
 * or a spreadsheet gave him — comma, tab or dash separated. Refusing that would
 * send him back to SQL, which is the thing this page exists to replace. But the
 * DATE is never guessed: anything that is not `YYYY-MM-DD` is reported with its
 * line number and nothing is sent, because a silently dropped line is a holiday
 * that quietly becomes an ordinary working day — and the register that reports
 * it will look like an attendance problem rather than a paste problem.
 */
export function parseHolidayPaste(text: string): { rows: HolidayDto[]; errors: string[] } {
  const rows: HolidayDto[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();

  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .forEach((line, index) => {
      if (line === '') return;
      const at = index + 1;
      const match = /^(\d{4}-\d{2}-\d{2})\s*(?:[,\t|]|\s[-–]\s)\s*(.+)$/.exec(line);
      if (match === null) {
        errors.push(`Line ${String(at)}: expected "YYYY-MM-DD, Name" — got "${line}".`);
        return;
      }
      const [, date, rawName] = match;
      const name = (rawName ?? '').trim();
      if (date === undefined || name === '') {
        errors.push(`Line ${String(at)}: a holiday needs a name.`);
        return;
      }
      if (seen.has(date)) {
        errors.push(`Line ${String(at)}: ${date} appears twice.`);
        return;
      }
      seen.add(date);
      rows.push({ date, name });
    });

  return { rows, errors };
}

function serializeHolidays(holidays: readonly HolidayDto[]): string {
  return holidays.map((holiday) => `${holiday.date}, ${holiday.name}`).join('\n');
}

/**
 * The editor, split out so the DRAFT IS OWNED BY A MOUNT — not by an effect.
 *
 * ⚠ THIS SPLIT IS A BUG FIX, NOT A TIDY-UP. The first version seeded the
 * textarea from the query inside a `useEffect`, which eslint refused
 * (`Calling setState synchronously within an effect`) — and the rule was right
 * about something worse than a render cascade: react-query refetches on window
 * focus, so an operator who typed half a year of holidays, alt-tabbed to the
 * government website they were copying from, and came back would have found
 * their typing REPLACED by whatever the server still held. Silent loss of an
 * hour's work.
 *
 * The parent renders this only once data has arrived and keys it on
 * `country:year:saves`, so the seed is taken exactly at the three moments it
 * should be — first load, a change of year, and a successful save — and a
 * background refetch cannot touch what is being typed.
 */
function HolidayEditor(props: {
  calendar: HolidayCalendarDto;
  year: number;
  saving: boolean;
  onSave: (rows: HolidayDto[]) => void;
}): React.JSX.Element {
  const [text, setText] = useState(() => serializeHolidays(props.calendar.holidays));
  const parsed = useMemo(() => parseHolidayPaste(text), [text]);
  const sharedWith = props.calendar.shared_with;

  return (
    <div className="flex flex-col gap-3">
      {/*
       * THE BLAST RADIUS, STATED BEFORE THE SAVE BUTTON, NOT AFTER.
       * Holidays are keyed by CALENDAR, not by country, so a country borrowing
       * another's calendar edits it for everyone reading it. The API returns
       * `shared_with` precisely so this can be said out loud — the surprise
       * here would be somebody else's country changing.
       */}
      {sharedWith.length === 0 ? null : (
        <Alert tone="warning">
          This calendar is also read by {sharedWith.join(', ')}. Saving changes what those countries
          see too.
        </Alert>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-th uppercase text-fg-secondary">
          One holiday per line — <span className="font-mono">YYYY-MM-DD, Name</span>
        </span>
        <textarea
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          rows={12}
          spellCheck={false}
          className="rounded-md border border-line bg-surface p-2 font-mono text-sm text-fg focus:outline-none focus:ring-2 focus:ring-focus"
          placeholder={'2027-01-26, Republic Day\n2027-08-15, Independence Day'}
        />
      </label>

      {parsed.errors.length === 0 ? null : (
        <Alert tone="danger">
          <span className="font-medium">Nothing will be sent.</span> Fix these lines first:
          <ul className="mt-1 list-disc pl-5">
            {parsed.errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => {
            props.onSave(parsed.rows);
          }}
          disabled={parsed.errors.length > 0 || props.saving}
        >
          {props.saving ? 'Saving…' : `Replace ${String(props.year)}`}
        </Button>
        <p className="text-sm text-fg-secondary">
          {/* Said plainly, because it is the one surprising thing about the verb. */}
          {parsed.rows.length === 0
            ? `Saving nothing clears ${String(props.year)} — every weekday becomes a working day.`
            : `${String(parsed.rows.length)} date(s). This replaces the whole of ${String(props.year)}; other years are untouched.`}
        </p>
      </div>
    </div>
  );
}

export function HolidaysPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const mayConfigure = isGroupAdmin || can(countryCode, 'config.manage');
  const mayManageUsers = isGroupAdmin || can(countryCode, 'users.manage');

  const [year, setYear] = useState<number>(() => new Date().getUTCFullYear());
  const [saves, setSaves] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const calendar = useQuery({
    queryKey: adminInfraKeys.holidays(countryCode, year),
    queryFn: ({ signal }) => fetchHolidays(countryCode, year, signal),
    enabled: mayConfigure,
  });

  const gaps = useQuery({
    queryKey: adminInfraKeys.calendarGaps(),
    queryFn: ({ signal }) => fetchCalendarGaps(signal),
    enabled: mayManageUsers,
  });

  const save = useMutation({
    mutationFn: (rows: HolidayDto[]) => replaceHolidays(countryCode, year, rows),
    onSuccess: (data) => {
      setActionError(null);
      setSaved(`Saved ${String(data.holidays.length)} date(s) for ${String(year)}.`);
      /*
       * ⚠ THE CACHE IS WRITTEN FROM THE RESPONSE **BEFORE** THE RE-KEY, AND THAT
       * ORDER IS THE WHOLE FIX. Caught live, not reasoned about: saving four
       * dates reported "Saved 4 date(s)" and then redrew the textarea EMPTY,
       * because bumping `saves` remounts the editor immediately while
       * `invalidateQueries` only schedules a refetch — so the fresh mount seeded
       * itself from the STALE pre-save cache. The database was right and the
       * screen was wrong, and the next press of Replace would have written that
       * wrong screen back over the year.
       *
       * `setQueryData` makes the cache true synchronously, so the remount in the
       * same render seeds from what the server actually stored. The invalidation
       * still follows, to reconcile anything the response did not carry.
       */
      queryClient.setQueryData(adminInfraKeys.holidays(countryCode, year), data);
      setSaves((n) => n + 1);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'holidays', countryCode] });
    },
    onError: (error: unknown) => {
      setSaved(null);
      setActionError(errorSentence(error, 'The calendar could not be saved.'));
    },
  });

  const fixGap = useMutation({
    /*
     * RULING 5 CALLOUT: this button used to write an OVERRIDE (a hardcoded
     * Mon–Fri week + this country's key). It now sets the PRIMARY COUNTRY —
     * the person inherits the country's own week and calendar, including
     * every future edit to them, instead of a snapshot that silently drifts.
     */
    mutationFn: (userId: string) =>
      setUserCalendar(userId, {
        primary_country: countryCode,
      }),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: adminInfraKeys.calendarGaps() });
    },
    onError: (error: unknown) => {
      setActionError(errorSentence(error, "That person's calendar could not be set."));
    },
  });

  if (!mayConfigure && !mayManageUsers) return <NoAdminAccess />;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Public holidays"
        subtitle="The working calendar behind the attendance register: the days nobody is expected in."
      />

      {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}
      {saved === null ? null : <Alert tone="info">{saved}</Alert>}

      {!mayConfigure ? null : (
        <Card>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <TextField
                label="Year"
                type="number"
                value={String(year)}
                onChange={(value) => {
                  const next = Number(value);
                  if (Number.isInteger(next) && next >= 2000 && next <= 2100) {
                    setYear(next);
                    setSaved(null);
                  }
                }}
              />
              <p className="pb-2 text-sm text-fg-secondary">
                Writing calendar{' '}
                <span className="font-mono">{calendar.data?.calendar_key ?? countryCode}</span>
              </p>
            </div>

            {calendar.isPending ? (
              <SkeletonRows rows={4} />
            ) : calendar.isError ? (
              /*
               * A READ FAILURE IS NOT AN EMPTY CALENDAR, and the difference is
               * the whole screen: one means "supply the dates", the other means
               * "the dates you supplied cannot be seen". Editing on top of a
               * failed read would let an operator save an empty year over a
               * full one, so the editor is not rendered at all.
               */
              <Alert tone="danger">
                {errorSentence(calendar.error, 'The calendar could not be read.')} This is a read
                failure, not an empty calendar — nothing is shown because nothing is known.
              </Alert>
            ) : (
              <HolidayEditor
                key={`${countryCode}:${String(year)}:${String(saves)}`}
                calendar={calendar.data}
                year={year}
                saving={save.isPending}
                onSave={(rows) => {
                  save.mutate(rows);
                }}
              />
            )}
          </div>
        </Card>
      )}

      {!mayManageUsers ? null : (
        <Card>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <TriangleAlert aria-hidden="true" className="size-4 shrink-0 text-warn-fg" />
              <h2 className="text-base font-medium text-fg">People with no working calendar</h2>
            </div>
            {/*
             * The owner's own framing: correct behaviour, and an operational
             * dead end. Their register reads `calendar_unresolved` for every day
             * until somebody sets this, and nobody would deduce that from
             * looking at a register that simply appears broken.
             */}
            <p className="text-sm text-fg-secondary">
              Their attendance register cannot say anything about a day until a calendar is set. A
              person holding several countries — or none — has no single country to inherit one
              from.
            </p>

            {gaps.isPending ? (
              <SkeletonRows rows={2} />
            ) : gaps.isError ? (
              <Alert tone="danger">
                {errorSentence(gaps.error, 'The list could not be read.')} This is a read failure,
                not an empty list.
              </Alert>
            ) : gaps.data.length === 0 ? (
              <EmptyState title="Everybody has a calendar" description="Nothing to fix here." />
            ) : (
              <ul className="flex flex-col gap-2">
                {gaps.data.map((gap: CalendarGapDto) => (
                  <li
                    key={gap.user_id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line p-2"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm text-fg">
                        {gap.name}
                        {gap.is_group_admin ? (
                          <span className="ml-2 text-th uppercase text-fg-secondary">
                            group admin
                          </span>
                        ) : null}
                      </span>
                      <span className="text-sm text-fg-secondary">
                        {gap.countries.length === 0
                          ? 'Holds no country'
                          : `Holds ${gap.countries.join(', ')}`}
                        {' · no primary country set'}
                      </span>
                    </div>
                    <Button
                      variant="quiet"
                      disabled={fixGap.isPending}
                      onClick={() => {
                        fixGap.mutate(gap.user_id);
                      }}
                    >
                      Set primary {countryCode}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
